import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IActiveFilter } from '@app/models';
import { emptyGuid } from '@app/helpers';

class UsersService {
    readonly apiBase: string = '/api/directory/People';

    resetUserPassword(userId: string, password: string, expire: boolean): AxiosPromise {
        return apiService.post(`${this.apiBase}(${userId})/SetPassword`, {
            password: password,
            expire: expire
        })
            .catch((error: AxiosResponse) => { return error; });
    }

    addUser(odataType: string, provisioningState: string, organizationId: string, emailAddress: string, displayName: string, principalName: string, attributes: any[], handleError: Function): AxiosPromise {
        return apiService.post(this.apiBase,
            {
                '@odata.type': odataType,
                ProvisioningState: provisioningState,
                OrganizationId: organizationId,
                EmailAddress: emailAddress,
                DisplayName: displayName,
                PrincipalName: principalName,
                Attributes: attributes
            },
            {
                onError: handleError
            })
            .catch();
    }

    importUsers(fileData: object, orgId: string, policyId: string): AxiosPromise {
        return apiService.post('api/import', fileData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'org': orgId,
                'policy': policyId
            }
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    isPrincipalNameAvailable(organizationId: string, principalName: string, tenantId: null): AxiosPromise {
        const apiUrl: string = this.apiBase + '/IsPrincipalNameAvailable';

        return apiService.post(apiUrl, {
            organizationId: organizationId,
            principalName: principalName,
            tenantId: tenantId
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getUsers(orderBy: string, asc: string, top: number, skip: number, organizationId: string, isReset: boolean, filters?: IActiveFilter[]): AxiosPromise {
        return apiService.get(`${this.apiBase}/PeopleDTO`, {
            params: {
                $orderBy: orderBy + ' ' + asc,
                $top: top,
                $skip: skip,
                $count: isReset,
                ...(filters ? { $filter: manageFilters(filters, organizationId) } : { $filter: manageFilters([], organizationId) })
            }
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getUser(userId: string): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')?$expand=Attributes,IdentityAuthenticators,Grants,AlternatePrincipalNames,Groups,MemberOf,SyncData';

        return apiService.get(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    setUserPassword(userId: string): AxiosPromise {
        return apiService.post(`${this.apiBase}(${userId})/SetPassword`, {
            password: '',
            expire: true
        })
            .catch((error: AxiosResponse) => { return error; });
    }

    removeRoleMembership(roleId: string, userId: string): AxiosPromise {
        const apiUrl: string = '/api/directory/Roles(' + roleId + ')/RemovePeople';

        return apiService.post(apiUrl, {
            People: [userId]
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    removeGroupMembership(groupId: string, userId: string): AxiosPromise {
        const apiUrl: string = '/api/directory/Groups(' + groupId + ')/RemovePeople';

        return apiService.post(apiUrl, {
            People: [userId]
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    deleteUsers(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/DeletePeople`;

        return apiService.post(apiUrl,
            jsonObject,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    disconnectUsers(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/DisconnectPeople`;

        return apiService.post(apiUrl,
            jsonObject,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    deleteUserPushToken(userId: string, pushTokenId: string): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/IdentityAuthenticators(' + pushTokenId + ')';

        return apiService.delete(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    deleteUserU2fToken(userId: string): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/RevokeToken';

        return apiService.post(apiUrl, { type: 'u2f' })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    deleteUserOtpToken(userId: string): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/RevokeToken';

        return apiService.post(apiUrl, { type: 'otp' })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    updateAttribute(userId: string, attributeId: string, attributeKey: string, attributeValue: string, handleError: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/attributes(' + attributeId + ')';

        return apiService.put(apiUrl, {
            Id: attributeId,
            Key: attributeKey,
            Value: attributeValue
        }, { onError: handleError })
            .catch();
    }

    createAttribute(userId: string, attributeKey: string, attributeValue: string, handleError: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/attributes';

        return apiService.post(apiUrl, {
            Key: attributeKey,
            Value: attributeValue
        }, { onError: handleError })
            .catch();
    }

    deleteAttribute(userId: string, attributeId: string, handleError: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/attributes(' + attributeId + ')';

        return apiService.delete(apiUrl, { onError: handleError })
            .catch();
    }

    addRoleForUser(userId: string, rolesForUser: any[]): AxiosPromise {
        const apiUrl: string = this.apiBase + '/AddRolesForUser';

        return apiService.post(apiUrl, {
            userId: userId,
            roleIds: rolesForUser.map(role => role.id)
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    addGroupForUser(userId: string, groupForUser: any[]): AxiosPromise {
        const apiUrl: string = this.apiBase + '/AddGroupsForUser';

        return apiService.post(apiUrl, {
            userId: userId,
            groupIds: groupForUser.map(group => group.id)
        }).catch((error: AxiosResponse) => Promise.reject(error));
    }

    getNonMemberedRoles(
        userId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/People(${userId})/NonMemberedRoles`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getNonMemberedGroups(
        userId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/People(${userId})/NonMemberedGroups`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    patchUser(userId: string, jsonObject: any, onSuccess?: Function, onError?: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')';

        return apiService.patch(apiUrl, jsonObject,
            {
                onSuccess: onSuccess,
                onError: onError
            });
    }

    addCustomClaimsForUser(jsonObject: any, onSuccess?: Function, onError?: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '/AddCustomClaimsForUser';

        return apiService.post(apiUrl, jsonObject,
            {
                onSuccess: onSuccess,
                onError: onError
            });
    }

    onboardingState(userId: string, onError: Function, onSuccess: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/OnboardingState';

        return apiService.get(apiUrl,
            {
                onSuccess: onSuccess,
                onError: onError
            })
            .catch();
    }

    sendOnboardEmail(userId: string, onError: Function, onSuccess: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/SendOnboardEmail';

        return apiService.post(apiUrl, null,
            {
                onSuccess: onSuccess,
                onError: onError
            })
            .catch();
    }

    // The following api's (from here down) are not tested yet. They might have to be changed when actual code uses them. - EL 11/15/2019

    onboardMany(jsonObject: any): AxiosPromise {
        const apiUrl: string = this.apiBase + '/SendOnboardEmailBulk';

        return apiService.post(apiUrl, jsonObject)
            .catch();
    }

    unlockUser(userId: string): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/Unlock';

        return apiService.post(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    deleteGrant(userId: string, grantId: string): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/Grants(' + grantId + ')';

        return apiService.delete(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    toggleImpersonation(userId: string, enabled: boolean, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.post(
            `${this.apiBase}(${userId})/SetImpersonation`,
            {
                enabled: enabled
            },
            {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getImpersonationGroups(userId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.get(`${this.apiBase}(${userId})/ImpersonationGroups`, {
            onError: handleError,
            onSuccess: handleSuccess
        })
            .catch();
    }

    getImpersonationGroupsNotMembered(
        userId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`/api/directory/People(${userId})/ImpersonationGroupsNotInGroup`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : {})
                }
            })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getImpersonators(userId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.get(`${this.apiBase}(${userId})/Impersonators?$expand=OnBehalfOf`,
            {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    addImpersonationGroups(userId: string, groupsIds: string[], handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.post(`${this.apiBase}(${userId})/AddImpersonationGroup`,
            {
                Group: groupsIds
            },
            {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    postMaxWindow(userId: string, value: number, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.post(`${this.apiBase}(${userId})/SetImpersonationWindow`,
            {
                window: value
            },
            {
                onError: handleError,
                onSuccess: handleSuccess
            }
        )
            .catch();
    }

    postExpire(userId: string, impersonatorID: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.post(
            `${this.apiBase}(${userId})/ExpireReservation`,
            {
                impersonatorId: impersonatorID
            },
            {
                onError: handleError,
                onSuccess: handleSuccess
            }
        )
            .catch();
    }

    postRemoveImpGroup(userId: string, groupID: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.post(
            `${this.apiBase}(${userId})/RemoveImpersonationGroup`,
            {
                groupId: groupID
            },
            {
                onError: handleError,
                onSuccess: handleSuccess
            }
        )
            .catch();
    }

    setAlternatePrincipalNames(userId: string, jsonObject: any): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/SetAlternatePrincipalNames';

        return apiService.post(apiUrl, jsonObject)
            .catch();
    }

    getUserPassPolicy(userId: string): AxiosPromise {
        const apiUrl: string = this.apiBase + '(' + userId + ')/PasswordPolicy';

        return apiService.get(apiUrl)
            .catch();
    }

    updatePeopleState(jsonObject: any, handleSuccess: Function, handleError: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '/UpdatePeopleState';

        return apiService.post(apiUrl, jsonObject,
            {
                onSuccess: handleSuccess,
                onError: handleError
            });
    }

    desyncUser(userId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService.patch(`${this.apiBase}(${userId})?$expand=Attributes`,
            {
                SyncSource: emptyGuid
            },
            {
                onError: handleError,
                onSuccess: handleSuccess
            }
        )
            .catch();
    }

    getUser2faOptions(userId: string): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${userId})/Get2faOptions`)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    postVerifyUserOtp(userId: string, passcode: string): AxiosPromise {
        return apiService
            .post(`${this.apiBase}(${userId})/VerifyOtp`,
                { "Passcode": passcode })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    postVerifyUserPush(userId: string): AxiosPromise {
        return apiService
            .post(`${this.apiBase}(${userId})/VerifyPush`)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getDarkWebCompromises(userId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${userId})/GetDarkWebCompromisesByEmail`;

        return apiService.get(apiUrl,
            {
                onError: handleError,
                onSuccess: handleSuccess
            });
    }
    getUserCustomClaims(userId: string, agentProfileId: string, handleSuccess?: Function, handleError?: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${userId})/IdentityConfigurations(${agentProfileId})`;

        return apiService.get(apiUrl,
            {
                onError: handleError,
                onSuccess: handleSuccess
            });
    }

    forceSignout(userId: string): AxiosPromise {
        const apiUrl = `${this.apiBase}(${userId})/SetForceSignout`;

            return apiService.post(apiUrl)
                .catch((error: AxiosResponse) => Promise.reject(error));
        }
}

export const usersService = new UsersService();
